/* eslint-disable max-lines */
import axios, { AxiosResponse } from 'axios';
import { CONFIGURATOR } from 'utils/constants';
import { GetTemplateWithDocType, GetTemplatesFields } from 'models/configurator';
import {
  AddTemplateFields,
  CloneCompanyBody,
  CloneUserRoleConfiguration,
  ConfigureNewRole,
  CreateCompanyBody,
  CreateExpActionTable,
  CreateNewTemplate,
  DeleteTemplateFields,
  EditCompanyBody,
  EditConfiguredRole,
  EditExpActionTable,
  GetNopoMicrocategories,
  GetNopoVendor,
  GetPoMicroCategoryRequest,
  GetPoVendorRequest,
  MicroCategory,
  RoleList,
  UpdateTemplateFields,
  WfCasual,
  WfDefinition,
  EditCorporateApproval,
  EditUserCompanyRole,
} from 'models/request';
import { trackPromise } from 'react-promise-tracker';
import {
  TableValue,
  UsersResponse,
  TablesDataResponse,
  GetTemplatePdfResponse,
  Company,
  SubjectDetailColumns,
  SubjectDetailRows,
  GetUserCompanyRoles,
  RoleProgramResponseList,
  GetUserForCompany,
  GetConfigureRoles,
  ConfiguredRole,
  DocumentType,
  CompanyActionTable,
  PoMicroCategoryList,
  PoVendorList,
  VendorNoPoList,
  CorporateApproval,
  Subject,
  AP2Template,
  AP2HTables,
  HTableValues,
  GetNopoMicrocategoriesResponse,
  GetNopoVendorResponse,
  WorkflowCompaniesDefinition,
  WorkflowCompaniesDefinitionList,
  GetCompanyWithTasks,
  CompanyGroups,
  NewWorkflowDefinition,
  EditWorkflowDefinition,
  WorkflowDefinitionTaskGroupsAssociations,
  NewCompanyGroups,
  OUserList,
  CompanyDefinitionsForAssociation,
  CompanyGroupsForTaskAssociation,
  CompanyTasksForAssociation,
  CreateAssociation,
  WfListOfConvalidationAssociation,
  CompanyCountryParams,
  WorkflowDefinitionTaskGroupsAssociationsExtended,
  TablesDataColumns,
  CompanyGroupsUser,
  TaskGroupAssociations,
  WorkflowCompaniesDefinitionListExtended,
} from 'models/response';
import { CreateAndSaveUserBody, CloneUserBody, AddDeleteUserCompanyRole } from 'models/request';
import useSWR from 'swr';
import { transformedRows } from 'routes/Configurator/routes/Workflow/routes/WorkflowCompanyDetails/tabs/Associations/utils';

/* -- USERS CONFIGURATOR -- */

/**
 * Create new user
 * @param {Object} body
 * @param {string} body.name
 * @param {string} body.username
 * @param {string} body.email
 * @param {number} body.sendMail
 * @param {number} body.groupDistribution
 * @param {string} body.loginType
 * @param {number} body.blocked
 * @param {string} body.expirationDate
 * @return {Promise<AxiosResponse>}
 */
const createNewUser = (body: CreateAndSaveUserBody) =>
  trackPromise(axios.post(`${CONFIGURATOR}/user/createNewUser`, body), 'module');

/**
 * Save user
 * @param {Object} body
 * @param {number} body.idUser
 * @param {string} body.name
 * @param {string} body.username
 * @param {string} body.email
 * @param {number} body.sendMail
 * @param {number} body.groupDistribution
 * @param {string} body.loginType
 * @param {number} body.changePassword
 * @param {number} body.blocked
 * @param {string} body.expirationDate
 * @return {Promise<AxiosResponse<UsersResponse>>}
 */
const saveUser = (body: CreateAndSaveUserBody) =>
  trackPromise(axios.post<UsersResponse>(`${CONFIGURATOR}/user/saveUser`, body), 'module');

/**
 * Import users
 * @param {Object} body
 * @param {string} body.base64Csv
 * @return {Promise<AxiosResponse<UsersResponse[]>>}
 */
const importUsers = (body: { base64Csv: string }) =>
  trackPromise(axios.post<UsersResponse[]>(`${CONFIGURATOR}/user/importUsersFromCsv`, body), 'module');

const blockUser = ({ idUser, forceBlock = false }: { idUser: number; forceBlock?: boolean }) =>
  trackPromise(
    axios.post<string>(`${CONFIGURATOR}/user/blockUser?idUser=${idUser}&forceBlock=${forceBlock}`),
    'module',
  );

const unblockUser = ({ idUser }: { idUser: number }) =>
  trackPromise(axios.post<string>(`${CONFIGURATOR}/user/unblockUser?idUser=${idUser}`), 'module');

/**
 * Clone user
 * @param {Object} body
 * @param {number} body.idUserToCopy
 * @param {string} body.emailNewUser
 * @param {string} body.usernameNewUser
 * @param {string} body.nameNewUser
 * @return {Promise<AxiosResponse<number>>}
 */
const cloneUser = (body: CloneUserBody) =>
  trackPromise(axios.post<number>(`${CONFIGURATOR}/user/cloneUser`, body), 'module');

/* -- TABLES CONFIGURATOR -- */

/* Gets Tables */
const getTables = () => trackPromise(axios.get<TableValue[]>(`${CONFIGURATOR}/table/getTables`), 'module');

/** Gets Tables Data
 * @param {string} tableToRetrieve
 * @param {number} idProgram
 * @return {Promise<AxiosResponse<TablesDataResponse>>}
 */
const getTablesData = (tableToRetrieve: string, idProgram: number) =>
  trackPromise(
    axios.get<TablesDataResponse>(`${CONFIGURATOR}/table/getTablesData`, { params: { tableToRetrieve, idProgram } }),
    'module',
  );

/**
 * This API updates a table
 * @param {Object} body
 * @param {string} body.table
 * @param {any[]} body.values
 * @return {Promise<AxiosResponse>}
 */
const updateTablesData = (body: { table: string; values: any[] }) =>
  trackPromise(axios.post(`${CONFIGURATOR}/table/updateTablesData`, body), 'module');

/**
 * This API insert a record for the table
 * @param {Object} body
 * @param {string} body.table
 * @param {any[]} body.values
 * @return {Promise<AxiosResponse>}
 */
const insertTablesData = (body: { table: string; values: any[] }) =>
  trackPromise(axios.post(`${CONFIGURATOR}/table/insertTablesData`, body), 'module');

/**
 * This API deletes some records from the table
 * @param {Object} body
 * @param {string} body.table
 * @param {any[]} body.values
 * @return {Promise<AxiosResponse>}
 */
const deleteTablesData = (body: { table: string; values: any[] }) =>
  trackPromise(axios.post(`${CONFIGURATOR}/table/deleteTablesData`, body), 'modal');

/**
 * api that export a table to a Bucket
 * @param {string} tableToRetrieve
 * @param {number} idProgram
 */
const exportTableToBucket = async (tableToRetrieve: string, idProgram: number) =>
  await trackPromise(
    axios.get('/config/table/exportTableToBucket', { params: { tableToRetrieve, idProgram } }),
    'module',
  );

/* -- TEMPLATES CONFIGURATOR -- */

/**
 * delete template, you need to pass the idTemplate and then return ok
 * @param {number} idTemplate
 * @return {Promise<AxiosResponse>}
 */
const deleteTemplate = (idTemplate: number) =>
  trackPromise(
    axios.post('/config/template/deleteTemplate', {
      idTemplate,
    }),
    'module',
  );

/**
 *  This API save a new template, it return the idTemplate
 * @param {OTemplate} template
 * @return {Promise<AxiosResponse<number>>}
 */
const createNewTemplate = (template: CreateNewTemplate) =>
  trackPromise(axios.post<number>('/config/template/createNewTemplate', template), 'module');

/**
 * this API return the list of templates fields
 * @return {Promise<AxiosResponse<GetTemplateWithDocType>>}
 */
const getTemplatesWithDocType = () =>
  trackPromise(axios.get<GetTemplateWithDocType[]>('/config/template/getTemplatesWithDocType'));

/**
 * this API return the list of templates fields
 * @param {number} idTemplate
 * @return {Promise<AxiosResponse<GetTemplatesFields>>}
 */
const getTemplateFields = (idTemplate: number) =>
  trackPromise(axios.get<GetTemplatesFields>('/config/template/getTemplateFields', { params: { idTemplate } }));

/**
 * this API log the base64 and the json of the template
 * @param {number} idTemplate the id of the template
 * @return {Promise<AxiosResponse<GetTemplatePdfResponse>>}
 */
const getTemplatePdf = (idTemplate: number): Promise<AxiosResponse<GetTemplatePdfResponse>> =>
  trackPromise(
    axios.get('/config/template/getTemplateDocuments', {
      params: { idTemplate },
    }),
    'module',
  );

/**
 * This API is used to edit template
 * @param {number} idTemplate
 * @param {string} templateName
 * @return {Promise<AxiosResponse>}
 */
const editTemplate = (idTemplate: number, templateName: string) =>
  trackPromise(axios.post('/config/template/editTemplate', { idTemplate, templateName }), 'module');

/**
 * This api that add a new field into the template
 * @param {UpdateTemplateFields} templateFields
 * @return {Promise<AxiosResponse>}
 */
const updateTemplateFields = (templateFields: UpdateTemplateFields) =>
  trackPromise(axios.post('/config/template/editTemplateField', templateFields), 'module');

/**
 * This api that delete one or more fields of a template
 * @param {DeleteTemplateFields} deleteTemplateFields
 * @return {Promise<AxiosResponse>}
 */
const deleteTemplateFields = async (deleteTemplateFields: DeleteTemplateFields) =>
  await trackPromise(axios.post('/config/template/deleteTemplateField', deleteTemplateFields), 'module');

/**
 * This api add a row into  the template
 * @param {AddTemplateFields} TemplateFieldsAndColumnFields
 */
const addTemplateFields = async (TemplateFieldsAndColumnFields: AddTemplateFields) =>
  await trackPromise(axios.post('/config/template/createNewTemplateField', TemplateFieldsAndColumnFields), 'module');

/* -- COMPANIES CONFIGURATOR -- */

/**
 * This API is called to get the subject details
 * @param {number} idSubject
 * @return {Promise<AxiosResponse<SubjectDetailRows>>}
 */
const getSubjectDetail = (idSubject: number) =>
  trackPromise(axios.get<SubjectDetailRows>(`${CONFIGURATOR}/vendor/getSubjectDetail?idSubject=${idSubject}`), 'modal');

/**
 * This API is called to get the subject details
 * @param {number} idSubject
 * @return {Promise<AxiosResponse<SubjectDetailColumns[]>>}
 */
const getSubjectDetailColumns = (idSubject: number) =>
  trackPromise(
    axios.get<SubjectDetailColumns[]>(`${CONFIGURATOR}/vendor/getSubjectDetailColumns?idSubject=${idSubject}`),
    'modal',
  );

/**
 * This API is called to get the subject details
 * @param {number} idSubject
 * @return {Promise<AxiosResponse<SubjectDetailColumns[]>>}
 */
const getSubjectDetailAllColumns = (idSubject: number) =>
  trackPromise(
    axios.get<SubjectDetailColumns[]>(`${CONFIGURATOR}/vendor/getSubjectDetailAllColumns?idSubject=${idSubject}`),
    'modal',
  );

/**
 * Clone company
 * @param {Object} body
 * @param {number} body.idCompanyToClone
 * @param {string} body.nameNewCompany
 * @return {Promise<AxiosResponse<Company>>}
 */
const cloneCompany = (body: CloneCompanyBody) =>
  trackPromise(axios.post<Company>(`${CONFIGURATOR}/company/cloneCompany`, body), 'module');

/**
 * Create company
 * @param {Object} body
 * @param {number} body.name
 * @param {string} body.label
 * @param {string} body.vatNumber
 * @param {string} body.socialReason
 * @param {string} body.fiscalCode
 * @param {string} body.phone
 * @param {string} body.fax
 * @param {string} body.email
 * @param {string} body.url
 * @param {string} body.onlineCode
 * @param {number} body.referenceUser
 * @param {string} body.counterPrefix
 * @param {string} body.country

 * @return {Promise<AxiosResponse<CreateCompanyBody>>}
 */
const createCompany = (body: CreateCompanyBody) =>
  trackPromise(axios.post<number>(`${CONFIGURATOR}/company/createNewCompany`, body), 'module');

/**
 * Save company
 * @param {Object} body
 * @param {number} body.idCompany
 * @param {number} body.name
 * @param {string} body.label
 * @param {string} body.vatNumber
 * @param {string} body.socialReason
 * @param {string} body.fiscalCode
 * @param {string} body.phone
 * @param {string} body.fax
 * @param {string} body.email
 * @param {string} body.url
 * @param {string} body.onlineCode
 * @param {number} body.referenceUser
 * @param {string} body.counterPrefix
 * @param {string} body.country
 * @return {Promise<AxiosResponse<Company>>}
 */
const saveCompany = (body: EditCompanyBody) =>
  trackPromise(axios.post<EditCompanyBody>(`${CONFIGURATOR}/company/saveCompany`, body), 'module');

/**
 * Get document type
 * @return {Promise<AxiosResponse<DocumentType[]>>}
 */
const getDocumentTypes = (): Promise<AxiosResponse<DocumentType[]>> =>
  trackPromise(axios.get(`${CONFIGURATOR}/company/getDocumentTypes`), 'module');

/* -- ROLES USER COMPANY CONFIGURATOR -- */

/**
 * this API gets all Users for Company Roles
 * @param {number} idCompany the id of the company
 * @return {Promise<AxiosResponse<GetUserCompanyRoles>>}
 */
const getUserCompanyRoles = (idCompany: number): Promise<AxiosResponse<GetUserCompanyRoles>> =>
  trackPromise(
    axios.get(`${CONFIGURATOR}/company/getUserCompanyRoles`, {
      params: { idCompany },
    }),
    'module',
  );

/**
 * this API gets all Users available roles
 * @param {number} idCompany the id of the company
 * @param {number} idUser the id of the user
 * @param {number | undefined} idRoleToEdit indicates the idRole to edit.
 * @return {Promise<AxiosResponse<RoleProgramResponseList[]>>}
 */
const getUserAvailableRoles = (
  idCompany: number,
  idUser: number,
  idRoleToEdit?: number,
): Promise<AxiosResponse<RoleProgramResponseList[]>> =>
  trackPromise(
    axios.get(`${CONFIGURATOR}/company/getUserAvailableRoles`, {
      params: { idCompany, idUser, idRoleToEdit },
    }),
    'module',
  );

/**
 * This api is used to save the added role for the user
 * @param {AddDeleteUserCompanyRole[]} SaveUserCompanyRoles
 */
const addUserCompanyRoles = async (SaveUserCompanyRoles: AddDeleteUserCompanyRole[]) =>
  await trackPromise(axios.post(`${CONFIGURATOR}/company/addUserCompanyRoles`, SaveUserCompanyRoles), 'module');

/**
 * This api is used to edit role
 * @param {EditUserCompanyRole[]} EditUserRole
 */
const editUserCompanyRoles = async (EditUserRole: EditUserCompanyRole[]) =>
  await trackPromise(axios.post(`${CONFIGURATOR}/company/editUserCompanyRoles`, EditUserRole), 'module');

/**
 * This api is used to delete the role for the user
 * @param {AddDeleteUserCompanyRole[]} DeleteUserCompanyRoles
 */
const deleteUserCompanyRoles = async (DeleteUserCompanyRoles: AddDeleteUserCompanyRole[]) =>
  await trackPromise(axios.post(`${CONFIGURATOR}/company/deleteUserCompanyRoles`, DeleteUserCompanyRoles), 'module');

/**
 * this API gets all Users available for the modal Add User
 * @param {number} idCompany the id of the company
 * @return {Promise<AxiosResponse<GetUserForCompany[]>>}
 */
const getUsersForCompany = (idCompany: number): Promise<AxiosResponse<GetUserForCompany[]>> =>
  trackPromise(
    axios.get(`${CONFIGURATOR}/company/getUsersForCompany`, {
      params: { idCompany },
    }),
    'modal',
  );

/* -- USERS COMPANIES ROLES CONFIGURATOR -- */

/**
 * this API gets all configure roles available for the modal Configure Roles
 * @return {Promise<AxiosResponse<GetConfigureRoles>>}
 */
const getConfigureRoles = async (): Promise<AxiosResponse<GetConfigureRoles>> =>
  await trackPromise(axios.get(`${CONFIGURATOR}/company/configureRoles`), 'modal');

/**
 *  This API configure a new role, it return the role created
 * @param {ConfigureNewRole} newRole
 * @return {Promise<AxiosResponse<ConfiguredRole>>}
 */
const configureNewRole = (newRole: ConfigureNewRole) =>
  trackPromise(axios.post<ConfiguredRole>(`${CONFIGURATOR}/company/configureNewRole`, newRole), 'modal');

/**
 *  This API edit a configured role, it return the role created
 * @param {EditConfiguredRole[]} editRole
 * @return {Promise<AxiosResponse<ConfiguredRole>>}
 */
const editConfiguredRole = (editRole: EditConfiguredRole[]) =>
  trackPromise(axios.post<ConfiguredRole>(`${CONFIGURATOR}/company/editRoleConfig`, editRole), 'modal');

/**
 * Clone role configuration
 * @param {CloneUserRoleConfiguration} clonedRole
 * @return {Promise<AxiosResponse<UserRoleResponsesList>>}
 */
const cloneUserRoleConfiguration = (clonedRole: CloneUserRoleConfiguration) =>
  trackPromise(axios.post(`${CONFIGURATOR}/company/cloneRoleConfiguration`, clonedRole), 'modal');

/**
 * this API gets all countries available for configure companies
 * @return {Promise<AxiosResponse<GetConfigureRoles>>}
 */
const getCountryParams = async (): Promise<AxiosResponse<CompanyCountryParams[]>> =>
  await trackPromise(axios.get(`${CONFIGURATOR}/company/getCountryParams`), 'module');

/* -- ACTION TABLE -- */

/**
 * this API gets all Companies available for the action table selected
 * @param {string} fluxName the name of the flux
 * @param {number[]} idCompany the id of the companies selected
 * @return {Promise<AxiosResponse<CompanyActionTable[]>>}
 */
const getHeaderConfig = (fluxName: string, idCompany: string) =>
  trackPromise(
    axios.get<CompanyActionTable[]>(`${CONFIGURATOR}/action/getHeaderConfig`, {
      params: { fluxName, idCompany },
    }),
    'module',
  );

/**
 * this API gets all vendors
 * @param {GetPoVendorRequest} request
 * @return {Promise<AxiosResponse<PoVendorList[]>>}
 */
const getPoVendors = (request: GetPoVendorRequest): Promise<AxiosResponse<PoVendorList[]>> =>
  trackPromise(axios.post(`${CONFIGURATOR}/action/getPOVendor`, request), 'app');
/**
 * this API gets all microcategories
 * @param {GetPoMicroCategoryRequest} request
 * @return {Promise<AxiosResponse<PoMicroCategoryList[]>>}
 */
const getPoMicrocategory = (request: GetPoMicroCategoryRequest): Promise<AxiosResponse<PoMicroCategoryList[]>> =>
  trackPromise(axios.post(`${CONFIGURATOR}/action/getPOMicrocategory`, request), 'app');

/**
 * this API gets all microcategories
 * @param {number} idUser the id of the user
 * @return {Promise<AxiosResponse<RoleList[]>>}
 */
const getUserRoles = (idUser: number): Promise<AxiosResponse<RoleList[]>> =>
  trackPromise(
    axios.get<RoleList[]>(`${CONFIGURATOR}/user/getUserRoles`, {
      params: { idUser },
    }),
    'module',
  );

/**
 * this API gets all nopo actions
 * @param {string[]} companyNames the name of the company
 * @return {Promise<AxiosResponse<VendorNoPoList[]>>}
 */

const getNoPoActionTable = (companyNames: string[]) =>
  trackPromise(axios.post<VendorNoPoList[]>(`${CONFIGURATOR}/action/getNoPO`, { companyNames }), 'module');

/**
 * this API edit a row of po action table
 * @param {EditPoActionTable} editExpActionTable
 * @return {Promise<AxiosResponse<EditPoActionTable>>}
 */
const editExpActionTable = (editExpActionTable: EditExpActionTable) =>
  trackPromise(axios.post<string[]>(`${CONFIGURATOR}/action/editExpAction`, editExpActionTable), 'module');

/**
 * this API create a new row of po action table
 * @param {CreateExpActionTable} createExpActionTable
 * @return {Promise<AxiosResponse<number>>}
 */

const createExpActionTable = (createExpActionTable: CreateExpActionTable) =>
  trackPromise(axios.post<number>(`${CONFIGURATOR}/action/createNewExpAction`, createExpActionTable), 'module');

/**
 * this API delete a row of po action table
 * @param {number} idExpAction
 * @return {Promise<AxiosResponse<number>>}
 */
const deleteExpActionTable = (idExpAction: number) =>
  trackPromise(axios.post(`${CONFIGURATOR}/action/deleteExpAction`, { idExpAction }), 'module');

/**
 * this API gets all casual of a company
 * @param {number} idCompany the id of the company
 * @return {Promise<AxiosResponse<WfCasual[]>>}
 */
const getWfCasuals = (idCompany: number) =>
  trackPromise(
    axios.get<WfCasual[]>(`${CONFIGURATOR}/action/getCompanyWFCausals`, {
      params: { idCompany },
    }),
    'module',
  );

/**
 * this API gets all definitions of a company
 * @param {number} idCompany the id of the company
 * @return {Promise<AxiosResponse<WfDefinition[]>>}
 */
const getWfDefinitions = (idCompany: number) =>
  trackPromise(
    axios.get<WfDefinition[]>(`${CONFIGURATOR}/action/getCompanyWFDefinitions`, {
      params: { idCompany },
    }),
    'module',
  );

/**
 * this API gets all subjects of a company
 * @param {number} idCompany the id of the company
 * @return {Promise<AxiosResponse<Subject[]>>}
 */
const getAllSubject = (idCompany: number) =>
  trackPromise(axios.get<Subject[]>(`${CONFIGURATOR}/action/getCompanySubjects`, { params: { idCompany } }), 'module');

const getCorporateApproval = async (): Promise<AxiosResponse<CorporateApproval[]>> =>
  await trackPromise(axios.get(`${CONFIGURATOR}/action/getCorporateApproval`), 'module');

const getAP2Template = (companyName: string, vendorCode: string) =>
  trackPromise(
    axios.get<AP2Template[]>(`${CONFIGURATOR}/action/getAP2Template`, {
      params: { companyName, vendorCode },
    }),
    'modal',
  );

const deleteAP2Template = (idAp2Template: string) =>
  trackPromise(axios.post<VendorNoPoList[]>(`${CONFIGURATOR}/action/deleteExpAP2Template`, { idAp2Template }), 'modal');

const getAP2HTables = () => trackPromise(axios.get<AP2HTables>(`${CONFIGURATOR}/action/getAP2HTables`), 'modal');

const getAP2HTable = (hTable: string, idCompany: number) =>
  trackPromise(
    axios.get<HTableValues[]>('/hTable/getHTable', {
      params: { hTable, idCompany },
    }),
    'modal',
  );

const createAP2Template = (payload: AP2HTables) =>
  trackPromise(axios.post<string>(`${CONFIGURATOR}/action/createAP2Template`, payload), 'modal');

const editAP2Template = (payload: AP2HTables) =>
  trackPromise(axios.post<AP2HTables>(`${CONFIGURATOR}/action/editExpAP2Template`, payload), 'modal');

const getAllMicrocategories = (idCompany: number) =>
  trackPromise(
    axios.get<MicroCategory[]>(`${CONFIGURATOR}/action/getCompanyMicrocategories`, { params: { idCompany } }),
    'module',
  );

const editCorporateApproval = async (payload: EditCorporateApproval) =>
  await trackPromise(axios.post(`${CONFIGURATOR}/action/editCorporateApproval`, payload), 'module');

const getNopoMicrocategories = (param: GetNopoMicrocategories) =>
  trackPromise(
    axios.post<GetNopoMicrocategoriesResponse[]>(`${CONFIGURATOR}/action/getNoPOMicrocategory`, param),
    'module',
  );

const getNopoVendor = (param: GetNopoVendor) =>
  trackPromise(axios.post<GetNopoVendorResponse[]>(`${CONFIGURATOR}/action/getNoPOVendor`, param), 'module');
type FluxType = 'NOPO' | 'PO';
const fluxes = (fluxType: FluxType) =>
  trackPromise(
    axios.get<string[]>(`${CONFIGURATOR}/action/getFluxNames`, {
      params: { fluxType },
    }),
    'module',
  );

// using SWR with axios call
const useFluxes = (fluxType: FluxType) => {
  const { data: response } = useSWR(fluxType, () => fluxes(fluxType), {
    revalidateIfStale: false,
    revalidateOnFocus: false,
    revalidateOnReconnect: false,
  });
  return response ? (response.data ?? []).map((flux) => ({ label: flux, value: flux })) : [];
};

/* -- WORKFLOW -- */

const getCompanyDefinitionTaskGroups = (idCompanies: number[]) =>
  trackPromise(
    axios.post<WorkflowCompaniesDefinition[]>(`${CONFIGURATOR}/workflow/getCompanyDefinitionTaskGroups`, {
      idCompanies,
    }),
    'module',
  );

const getCompanyDefinitions = async (idCompany: number) =>
  await trackPromise(
    axios.get<WorkflowCompaniesDefinitionList[]>(`${CONFIGURATOR}/workflow/getCompanyDefinitions`, {
      params: { idCompany },
    }),
    'module',
  );

const getCompanyWithTasks = () =>
  trackPromise(axios.get<GetCompanyWithTasks[]>(`${CONFIGURATOR}/workflow/getCompanyTasks`), 'modal');
// swr fetcher
const useGetCompanyWithTasks = (companiesDefinition?: WorkflowCompaniesDefinitionListExtended | null) => {
  const { data, mutate } = useSWR<GetCompanyWithTasks[]>(
    companiesDefinition ? ['getTasksKey', companiesDefinition.idCompany] : null,
    async () => {
      const response = await getCompanyWithTasks();
      return response?.data ?? [];
    },
    {
      revalidateIfStale: false,
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
    },
  );

  return { tasks: data, revalidate: mutate };
};

const getCompanyGroups = (idCompany: number) =>
  trackPromise(
    axios.get<CompanyGroups[]>(`${CONFIGURATOR}/workflow/getCompanyGroups`, {
      params: { idCompany },
    }),
    'module',
  );

const useGetCompanyGroups = (getCompanyGroupsKey: 'getCompanyGroupsKey', idCompany: number | undefined) => {
  const { data, mutate } = useSWR<CompanyGroups[]>(
    getCompanyGroupsKey,
    async () => {
      const response = getCompanyGroupsKey && idCompany ? await getCompanyGroups(idCompany) : null;
      return response?.data ?? [];
    },
    {
      revalidateIfStale: false,
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
    },
  );

  return { groups: data, revalidate: mutate };
};

const getCompanyGroupUsers = (idCompany: number, idGroup: number) =>
  trackPromise(
    axios.get<CompanyGroupsUser>(`${CONFIGURATOR}/workflow/getCompanyGroupUsers`, {
      params: { idCompany, idGroup },
    }),
    'module',
  );

/**
 * delete definition, you need to pass the idDefinition
 * @param {number} idDefinition
 * @return {Promise<AxiosResponse>}
 */
const deleteWfDefinition = (idDefinition: number) =>
  trackPromise(
    axios.post('/config/workflow/deleteWfDefinition', {
      idDefinition,
    }),
    'module',
  );

/**
 * clone definition, you need to pass the idDefinitionToClone and wfName
 * @param {number} idDefinitionToCopy
 * @param {string} wfName
 * @return {Promise<AxiosResponse>}
 */
const cloneWfDefinition = (idDefinitionToCopy: number, wfName: string) =>
  trackPromise(
    axios.post('/config/workflow/cloneWfDefinition', {
      idDefinitionToCopy,
      wfName,
    }),
    'module',
  );

const createWfDefinition = (payload: NewWorkflowDefinition) =>
  trackPromise(axios.post(`${CONFIGURATOR}/workflow/createNewWfDefinition`, payload), 'modal');

const editWfDefinition = (payload: EditWorkflowDefinition) =>
  trackPromise(axios.post(`${CONFIGURATOR}/workflow/editWfDefinition`, payload), 'modal');

const getDefinitionTaskGroupsAssociations = (idCompany: number) =>
  trackPromise(
    axios.get<WorkflowDefinitionTaskGroupsAssociations[]>(`${CONFIGURATOR}/workflow/getDefinitionTaskAssociations`, {
      params: { idCompany },
    }),
    'module',
  );

const getTaskGroupAssociations = (idCompany: number, idTaskDefinition: number) =>
  trackPromise(
    axios.get<TaskGroupAssociations[]>(`${CONFIGURATOR}/workflow/getTaskGroupAssociations`, {
      params: { idCompany, idTaskDefinition },
    }),
    'module',
  );

const useGetDefinitionTaskGroupsAssociations = (
  getDefinitionTaskGroupsAssociationsKey: 'getDefinitionTaskGroupsAssociationsKey',
  idCompany: number | undefined,
) => {
  const { data, mutate } = useSWR<WorkflowDefinitionTaskGroupsAssociationsExtended[]>(
    getDefinitionTaskGroupsAssociationsKey,
    async () => {
      const response =
        getDefinitionTaskGroupsAssociationsKey && idCompany
          ? await getDefinitionTaskGroupsAssociations(idCompany)
          : null;
      return response?.data ? transformedRows(response?.data) : [];
    },
    {
      revalidateIfStale: false,
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
    },
  );

  return { associations: data, revalidateAssociations: mutate };
};

const createWfTask = async (name: string, desc: string) => {
  return await trackPromise(
    axios.post<number>(`${CONFIGURATOR}/workflow/createNewWfTask`, {
      name,
      desc,
    }),
  );
};

const editWfTask = async (idTask: number, name: string, desc: string) => {
  await trackPromise(
    axios.post(`${CONFIGURATOR}/workflow/editWfTask`, {
      idTask,
      name,
      desc,
    }),
  );
};

const deleteWfTask = async (idTask: number) => {
  await trackPromise(
    axios.post(`${CONFIGURATOR}/workflow/deleteWfTask`, {
      idTask,
    }),
  );
};

// WF GROUP
const createWfGroup = (payload: NewCompanyGroups) =>
  trackPromise(axios.post(`${CONFIGURATOR}/workflow/createNewWfGroup`, payload), 'module');

const getUsersForGroup = (idCompany: number) =>
  trackPromise(
    axios.get<OUserList[]>(`${CONFIGURATOR}/workflow/getUsersForGroup`, {
      params: { idCompany },
    }),
    'module',
  );

const deleteWfGroup = (idGroup: number) =>
  trackPromise(
    axios.post('/config/workflow/deleteWfGroup', {
      idGroup,
    }),
    'module',
  );

const editWfGroup = (payload: CompanyGroups) =>
  trackPromise(axios.post(`${CONFIGURATOR}/workflow/editWfGroup`, payload), 'module');

const cloneWfGroup = (idGroupToCopy: number, groupName: string, groupDesc: string) =>
  trackPromise(
    axios.post('/config/workflow/cloneWfGroup', {
      idGroupToCopy,
      groupName,
      groupDesc,
    }),
    'module',
  );

// WF ASSOCIATIONS
const getCompanyDefinitionsForAssociation = (idCompany: number) =>
  trackPromise(
    axios.get<CompanyDefinitionsForAssociation[]>(`${CONFIGURATOR}/workflow/getCompanyDefinitionsForAssociation`, {
      params: { idCompany },
    }),
    'modal',
  );

const useGetCompanyDefinitionsForAssociation = (
  getCompanyDefinitionForAssociationsKey: 'getCompanyDefinitionForAssociationsKey',
  idCompany: number | undefined,
) => {
  const { data, mutate } = useSWR<CompanyDefinitionsForAssociation[]>(
    getCompanyDefinitionForAssociationsKey,
    async () => {
      const response =
        getCompanyDefinitionForAssociationsKey && idCompany
          ? await getCompanyDefinitionsForAssociation(idCompany)
          : null;
      return response?.data ?? [];
    },
    {
      revalidateIfStale: false,
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
    },
  );

  return { listOfDefinitionsForAssociations: data, revalidate: mutate };
};

const getCompanyTaskForAssociation = (idCompany: number, idDefinition: number, idTaskDefinition?: number) =>
  trackPromise(
    axios.get<CompanyTasksForAssociation[]>(`${CONFIGURATOR}/workflow/getCompanyTaskForAssociation`, {
      params: { idCompany, idDefinition, idTaskDefinition },
    }),
    'module',
  );

const useGetCompanyTaskForAssociation = (
  getCompanyTasksForAssociationsKey: 'getCompanyTasksForAssociationsKey',
  idCompany: number | undefined,
  idDefinition: number | undefined,
  idTaskDefinition?: number,
) => {
  const { data, mutate } = useSWR<CompanyTasksForAssociation[]>(
    getCompanyTasksForAssociationsKey,
    async () => {
      const response =
        getCompanyTasksForAssociationsKey && idCompany && idDefinition
          ? await getCompanyTaskForAssociation(idCompany, idDefinition, idTaskDefinition)
          : null;
      return response?.data ?? [];
    },
    {
      revalidateIfStale: false,
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
    },
  );

  return { listOfTasksForAssociations: data, revalidate: mutate };
};

const getGroupsForTaskAssociations = (idCompany: number) =>
  trackPromise(
    axios.get<CompanyGroupsForTaskAssociation[]>(`${CONFIGURATOR}/workflow/getGroupsForTaskAssociations`, {
      params: { idCompany },
    }),
    'module',
  );

const useGetGroupsForTaskAssociations = (
  getCompanyGroupsForAssociationsKey: 'getCompanyGroupsForAssociationsKey',
  idCompany: number | undefined,
) => {
  const { data, mutate } = useSWR<CompanyGroupsForTaskAssociation[]>(
    getCompanyGroupsForAssociationsKey,
    async () => {
      const response =
        getCompanyGroupsForAssociationsKey && idCompany ? await getGroupsForTaskAssociations(idCompany) : null;
      return response?.data ?? [];
    },
    {
      revalidateIfStale: false,
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
    },
  );

  return { listOfGroupsForAssociations: data, revalidate: mutate };
};

const addDefinitionTaskAssociations = (newAssociation: CreateAssociation) =>
  trackPromise(axios.post('/config/workflow/addDefinitionTaskAssociations', newAssociation), 'module');

const deleteDefinitionTaskAssociations = (
  idDefinition: number | undefined,
  idTaskDefinition: number | undefined | null,
) =>
  trackPromise(
    axios.post('/config/workflow/deleteDefinitionTaskAssociations', {
      idDefinition,
      idTaskDefinition,
    }),
    'modal',
  );

const getWfOConvalidationTaskActionAssociation = () =>
  trackPromise(
    axios.get<WfListOfConvalidationAssociation[]>(`${CONFIGURATOR}/workflow/getWfOConvalidationTaskAction`),
    'module',
  );

const useGetWfOConvalidationTaskActionAssociation = (getWfConvalidationKey: 'getWfConvalidationKey' | undefined) => {
  const { data: response } = useSWR(getWfConvalidationKey ? 'getWfOConvalidationTaskAction' : null, () =>
    getWfConvalidationKey ? getWfOConvalidationTaskActionAssociation() : null,
  );
  return response ? response.data : [];
};

const cloneDefinitionTaskAssociations = (idDefinitionSource: number | null, idDefinitionDestination: number | null) =>
  trackPromise(
    axios.post('/config/workflow/cloneDefinitionTaskAssociations', {
      idDefinitionSource,
      idDefinitionDestination,
    }),
    'modal',
  );

const editDefinitionTaskAssociations = (editedAssociation: CreateAssociation) =>
  trackPromise(axios.post('/config/workflow/editDefinitionTaskAssociations', editedAssociation), 'module');

const replaceUserMassivelyWfGroup = (idUserReplaced: number, idUserToReplace: number) =>
  trackPromise(
    axios.post('/config/workflow/replaceUserMassivelyWfGroup', {
      idUserReplaced,
      idUserToReplace,
    }),
    'modal',
  );
const removeUserMassivelyWfGroup = (idUser: number) =>
  trackPromise(
    axios.post('/config/workflow/removeUserMassivelyWfGroup', {
      idUser,
    }),
    'modal',
  );

const addUserMassivelyWfGroup = (idUserToCopy: number, idUserToAdd: number) =>
  trackPromise(
    axios.post('/config/workflow/addUserMassivelyWfGroup', {
      idUserToCopy,
      idUserToAdd,
    }),
    'modal',
  );

const getSubjectConfiguration = () =>
  trackPromise(axios.get<TablesDataColumns[]>('/subjectConfigurations/getSubjectConfigurations'), 'module');

const saveSubjectAndDetails = (subject: { [key: string]: any }) => {
  return trackPromise(axios.post('/config/vendor/saveSubjectAndDetails', { ...subject }), 'module');
};

export default {
  getTables,
  createNewUser,
  saveUser,
  importUsers,
  cloneUser,
  getTablesData,
  updateTablesData,
  insertTablesData,
  deleteTablesData,
  deleteTemplate,
  createNewTemplate,
  getTemplatesWithDocType,
  getTemplateFields,
  getTemplatePdf,
  editTemplate,
  updateTemplateFields,
  deleteTemplateFields,
  addTemplateFields,
  exportTableToBucket,
  getSubjectDetailColumns,
  getSubjectDetailAllColumns,
  getSubjectDetail,
  getUserCompanyRoles,
  getUserAvailableRoles,
  addUserCompanyRoles,
  deleteUserCompanyRoles,
  getUsersForCompany,
  getConfigureRoles,
  configureNewRole,
  editConfiguredRole,
  cloneCompany,
  createCompany,
  saveCompany,
  getDocumentTypes,
  cloneUserRoleConfiguration,
  getHeaderConfig,
  getPoMicrocategory,
  getPoVendors,
  getUserRoles,
  getCorporateApproval,
  getAP2Template,
  deleteAP2Template,
  getAP2HTables,
  getAP2HTable,
  createAP2Template,
  editAP2Template,
  editExpActionTable,
  createExpActionTable,
  deleteExpActionTable,
  getWfCasuals,
  getWfDefinitions,
  getAllMicrocategories,
  getAllSubject,
  editCorporateApproval,
  getNopoMicrocategories,
  getNopoVendor,
  useFluxes,
  getNoPoActionTable,
  getCompanyDefinitionTaskGroups,
  getCompanyDefinitions,
  getCompanyWithTasks,
  useGetCompanyWithTasks,
  getCompanyGroups,
  useGetCompanyGroups,
  deleteWfDefinition,
  cloneWfDefinition,
  createWfDefinition,
  editWfDefinition,
  useGetDefinitionTaskGroupsAssociations,
  createWfTask,
  editWfTask,
  deleteWfTask,
  createWfGroup,
  getUsersForGroup,
  deleteWfGroup,
  editWfGroup,
  cloneWfGroup,
  useGetCompanyDefinitionsForAssociation,
  useGetCompanyTaskForAssociation,
  useGetGroupsForTaskAssociations,
  addDefinitionTaskAssociations,
  deleteDefinitionTaskAssociations,
  useGetWfOConvalidationTaskActionAssociation,
  cloneDefinitionTaskAssociations,
  editDefinitionTaskAssociations,
  getCompanyTaskForAssociation,
  getTaskGroupAssociations,
  getCountryParams,
  editUserCompanyRoles,
  removeUserMassivelyWfGroup,
  addUserMassivelyWfGroup,
  replaceUserMassivelyWfGroup,
  getSubjectConfiguration,
  saveSubjectAndDetails,
  getCompanyGroupUsers,
  blockUser,
  unblockUser,
};
